import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import * as api from '../../services/api';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 40px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
  margin: 0 0 12px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  margin: 0 0 24px;
  line-height: 1.5;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
  margin-bottom: 16px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59,130,246,0.2);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: #3b82f6;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 16px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 13px;
  margin-bottom: 12px;
`;

const SuccessText = styled.div`
  color: #16a34a;
  font-size: 13px;
  margin-bottom: 12px;
  line-height: 1.5;
`;

const BackLink = styled(Link)`
  display: block;
  text-align: center;
  font-size: 14px;
  color: #6b7280;
  margin-top: 4px;
  text-decoration: none;
  &:hover { color: #374151; }
`;

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) { setError('Email is required.'); return; }
    setLoading(true);
    setError('');
    try {
      await api.requestPasswordReset(trimmed);
      setSent(true);
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Card>
        <Title>Reset Password</Title>
        <Subtitle>Enter your account email and we'll send you a reset link.</Subtitle>
        {sent ? (
          <>
            <SuccessText>
              If an account exists for that email, a password reset link has been sent. Check your inbox (and spam folder).
            </SuccessText>
            <BackLink to="/login">Back to sign in</BackLink>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <ErrorText>{error}</ErrorText>}
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </SubmitButton>
            <BackLink to="/login">Back to sign in</BackLink>
          </form>
        )}
      </Card>
    </Wrapper>
  );
};

export default ForgotPassword;
